import {
  EndOfNextPeriod,
  GetGranularityConfiguration,
  Granularity,
} from "@hydrologiq/common/types/reporting/Granularity"
import { useQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { IANATimeZone, toTimestamp, startOfDay, endOfDay } from "@hydrologiq/common/helpers/date"
import Reporting, { ReportingSkeleton } from "@hydrologiq/shared/reporting/Reporting"
import { DateTime, days } from "timezonecomplete"
import CustomerUsageDownload from "./CustomerUsageDownload"
import { useState } from "react"
import ReportingChart from "@hydrologiq/shared/reporting/ReportingChart"
import { ReportingChartSkeleton } from "@hydrologiq/shared/reporting/ReportingChart"
export type CustomerUsageView = "chart" | "table"

interface CustomerUsageProps {
  start: DateTime
  end: DateTime
  granularity: Granularity
  customerId: string
  customerName: string
  view: CustomerUsageView
  latest?: boolean
  title?: boolean
}

export function CustomerUsageSkeleton({ view, title }: { view: CustomerUsageView; title?: boolean }) {
  return view === "table" ? <ReportingSkeleton /> : <ReportingChartSkeleton title={title} />
}

function CustomerUsage({ start, end, customerId, customerName, granularity, view, latest, title }: CustomerUsageProps) {
  const [downloading, setDownloading] = useState<boolean>(false)
  const granularityConfig = GetGranularityConfiguration(granularity)

  let endOfPreview = EndOfNextPeriod(start, granularity, 10)
  if (endOfPreview.greaterThan(end)) endOfPreview = end

  const { loading, data } = useQuery(gqlTypes.GetCustomerUsageDocument, {
    variables: {
      id: customerId,
      start: toTimestamp(latest ? startOfDay(end.sub(days(9))) : start, true),
      end: toTimestamp(latest ? endOfDay(end) : endOfPreview, true),
      freq: granularityConfig.queryFrequency,
      tz: IANATimeZone(),
    },
  })

  return view === "table" ? (
    <Reporting
      start={start}
      end={end}
      granularity={granularity}
      onDataCallback={() => (data?.customerUsage ? data.customerUsage : [])}
      onDownload={() => setDownloading(true)}
      downloading={downloading}
      loading={loading}
    >
      <CustomerUsageDownload
        start={start}
        end={end}
        granularity={granularity}
        customer={customerId}
        customerName={customerName}
        callback={() => setDownloading(false)}
      />
    </Reporting>
  ) : (
    <ReportingChart
      onDataCallback={() => (data?.customerUsage ? data.customerUsage : [])}
      title={title}
      loading={loading}
    />
  )
}

export default CustomerUsage
